import React from "react";
import styles from "./MaterialsListPreview.module.scss";
import { X, Send, Info } from "lucide-react";
import { motion } from "framer-motion";
import EstimationDownloadActions from "./components/EstimationDownloadActions.tsx";
import ModalOverlay from "../../UI/ModalOverlay/ModalOverlay.tsx";

export interface MaterialPreview {
    id?: string | number;
    name: string;
    category: string;
    unit: string;
    quantity: number;
    price?: number;
}

interface MaterialsListPreviewProps {
    materials: MaterialPreview[];
    sendHandler: () => void;
    closePreview: () => void;
    isEstimacionDeCostos: boolean;
    setShowSendQuotePopup: (show: boolean) => void;
}

const variants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1 },
};

const MaterialsListPreview = ({
    materials,
    sendHandler,
    closePreview,
    isEstimacionDeCostos,
    setShowSendQuotePopup,
    autofillDraftId,
}: MaterialsListPreviewProps) => {
    const normalizeNumber = (value: number | null | undefined, fallback = 0): number => {
        if (value === null || value === undefined) return fallback;
        const parsed = Number(value);
        return Number.isNaN(parsed) ? fallback : parsed;
    };

    const formatNumber = (value: number, options?: Intl.NumberFormatOptions): string => {
        const normalized = normalizeNumber(value);
        return new Intl.NumberFormat("es-AR", options).format(normalized);
    };

    const calculateSubtotal = (price: number, quantity: number): number => {
        const normalizedPrice = normalizeNumber(price);
        const normalizedQuantity = normalizeNumber(quantity, 1);
        return normalizedPrice * normalizedQuantity;
    };

    const unitToShow = (unit: string, quantity: number = 1): string => {
        const singular: Record<string, string> = {
            m2: "Metro cuadrado",
            m3: "Metro cúbico",
            kg: "Kilogramo",
            ml: "Mililitro",
            cm: "Centímetro",
            u: "Unidad",
            l: "Litro",
            cm2: "Centímetro cuadrado",
            cm3: "Centímetro cúbico",
            gr: "Gramo",
            m: "Metro",
        };
        const plural: Record<string, string> = {
            m2: "Metros cuadrados",
            m3: "Metros cúbicos",
            kg: "Kilogramos",
            ml: "Mililitros",
            cm: "Centímetros",
            u: "Unidades",
            l: "Litros",
            cm2: "Centímetros cuadrados",
            cm3: "Centímetros cúbicos",
            gr: "Gramos",
            m: "Metros",
        };
        const q = normalizeNumber(quantity, 1);
        const map = q === 1 ? singular : plural;
        return map[unit] ?? unit;
    };

    const totalEstimado = materials.reduce(
        (acc, curr) => acc + calculateSubtotal(curr.price ?? 0, curr.quantity),
        0
    );
    const columnCount = isEstimacionDeCostos ? 5 : 3;
    const quantityWithUnit = (qty: number, unit: string) => `${formatNumber(qty)} ${unitToShow(unit, qty)}`;
    const previewTitle = isEstimacionDeCostos
        ? "Vista previa de tu estimación"
        : "Vista previa para proveedores";
    const previewIntro = isEstimacionDeCostos
        ? `${materials.length} ${materials.length === 1 ? "material" : "materiales"} · Total estimado ${formatNumber(totalEstimado, { style: "currency", currency: "ARS" })}`
        : `${materials.length} ${materials.length === 1 ? "material" : "materiales"} en tu lista. Los proveedores verán esta información para enviarte presupuestos.`;

    return (
        <ModalOverlay isOpen={true} onClose={closePreview}>
            <motion.div
                className={styles.container}
                initial="hidden"
                animate="visible"
                variants={variants}
            >
                <div className={styles.container_header}>
                    <div className={styles.container_header_text}>
                        <h2 className={styles.container_header_title}>{previewTitle}</h2>
                        <p className={styles.container_header_intro}>{previewIntro}</p>
                    </div>
                    <X size={20} onClick={closePreview} className={styles.container_header_closeIcon} />
                </div>
                <div className={styles.container_tableContainer}>
                    {materials.length === 0 ? (
                        <div className={styles.container_empty}>
                            <p className={styles.container_empty_text}>No hay materiales para mostrar. Agregá ítems desde el panel y volvé a abrir la vista previa.</p>
                        </div>
                    ) : (
                    <table className={styles.container_table}>
                        <thead className={styles.container_table_head}>
                            <tr>
                                <th className={styles.container_table_head_item}>Material</th>
                                <th className={styles.container_table_head_item}>Categoría</th>
                                <th className={styles.container_table_head_item}>Cantidad</th>
                                {isEstimacionDeCostos && (
                                    <>
                                        <th className={styles.container_table_head_item}>Precio unit.</th>
                                        <th className={styles.container_table_head_item}>Subtotal</th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {materials.map((material, index) => (
                                <tr className={styles.container_table_borderBottom} key={material.id ?? index}>
                                    <td className={styles.container_table_item}>{material.name}</td>
                                    <td className={styles.container_table_item}>{material.category}</td>
                                    <td className={styles.container_table_item}>{quantityWithUnit(material.quantity, material.unit)}</td>
                                    {isEstimacionDeCostos && (
                                        <>
                                            <td className={styles.container_table_item}>
                                                {formatNumber(material.price ?? 0, { style: "currency", currency: "ARS" })}
                                            </td>
                                            <td className={styles.container_table_item}>
                                                {formatNumber(
                                                    calculateSubtotal(material.price ?? 0, material.quantity),
                                                    { style: "currency", currency: "ARS" }
                                                )}
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className={styles.container_footer}>
                                {isEstimacionDeCostos ? (
                                    <>
                                        <td colSpan={columnCount - 1} className={styles.container_footer_total}>Total estimado</td>
                                        <td className={styles.container_footer_number}>
                                            {formatNumber(totalEstimado, { style: "currency", currency: "ARS" })}
                                        </td>
                                    </>
                                ) : (
                                    <td colSpan={columnCount} className={`${styles.container_footer_total} ${styles.container_footer_total_span}`}>
                                        Total: {materials.length} {materials.length === 1 ? "material" : "materiales"}
                                    </td>
                                )}
                            </tr>
                        </tfoot>
                    </table>
                    )}
                </div>
                <div className={styles.container_buttonsContainer}>
                    {isEstimacionDeCostos && (
                        <div className={styles.container_buttonsContainer_disclaimer}>
                            <Info size={16} className={styles.container_buttonsContainer_disclaimer_icon} />
                            <p>Los precios son orientativos y no representan una oferta formal.</p>
                        </div>
                    )}
                    {!isEstimacionDeCostos && materials.length > 0 && (
                        <p className={styles.container_buttonsContainer_hint}>
                            Al enviar, los proveedores de tu zona podrán cotizar sobre esta lista.
                        </p>
                    )}
                    <div className={styles.container_buttonsContainer_buttons}>
                        <button className={styles.container_buttonsContainer_buttonCancel} onClick={closePreview}>
                            Cancelar
                        </button>
                        {isEstimacionDeCostos ? (
                            <EstimationDownloadActions
                                closePreview={closePreview}
                                previewPopUp={true}
                                materials={materials}
                                setShowSendQuotePopup={setShowSendQuotePopup}
                            />
                        ) : (
                            <button
                                className={styles.container_buttonsContainer_buttonSend}
                                disabled={materials.length === 0}
                                onClick={sendHandler}
                            >
                                <Send size={16} />
                                Enviar para cotizar
                            </button>
                        )}
                    </div>
                </div>
            </motion.div>
        </ModalOverlay>
    );
};

export default MaterialsListPreview;
